import Image from 'next/image';
import Heading from '@components/ui/Heading';
import IconWrapper from '@components/ui/Icons';
import {
  CategoryNavImage,
  CategoryNavLinks,
  PageType,
} from '@adapters/prismic/types/slices';
import { eePromoImpHandler, setPromoDetailsCookie } from '@utils/gtm';
import { useEffect, useMemo, useRef } from 'react';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { useLocale } from '@hooks/useLocale';
import styles from './CategoryNavigation.module.scss';

// We aren't breaking these rules as its just analytics not functionality
// The errors only throw because href has been moved to Link parent component
/* tslint:disable:jsx-a11y/click-events-have-key-events jsx-a11y/no-static-element-interactions */

const CategoryNavListImage = ({ image }: { image: CategoryNavImage }) => (
  <Image
    src={image?.url}
    alt={image?.alt}
    quality={65}
    fill
    sizes="(min-width: 720px) 320px, 480px"
  />
);

export interface CategoryNavigationItemProps {
  title?: string;
  image?: CategoryNavImage;
  link?: string;
  trackPromos?: boolean;
  pageType?: PageType;
  navLinks?: CategoryNavLinks[];
  text?: string;
  sliceIndex: number;
  positionWithinSlice: number;
}

const CategoryNavigationItem = ({
  title,
  image,
  link,
  trackPromos,
  pageType,
  navLinks,
  text,
  positionWithinSlice,
  sliceIndex,
}: CategoryNavigationItemProps) => {
  const locale = useLocale();
  const ref = useRef<any>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      id: title,
      name: `${title}-${pageType}-merchbox`,
      type: 'link',
      url: link,
      position: `${pageType}-${sliceIndex}-CategoryNavigation-${positionWithinSlice}`,
    }),
    [link, pageType, positionWithinSlice, sliceIndex, title]
  );

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);
    }
  }, [isVisible, promoDetails, trackPromos]);

  const paddingCalc =
    image?.dimensions.height && image?.dimensions.width
      ? `calc(100% * ${image.dimensions.height}/${image.dimensions.width})`
      : undefined;

  return (
    <div className={styles.categoryNav} key={`category-nav-${title}`}>
      <div className={styles.categoryNavInner}>
        <div
          className={styles.categoryNavImage}
          style={{
            paddingTop: paddingCalc,
          }}
        >
          {link ? (
            <a
              href={`/${locale}${link}`}
              onClick={
                trackPromos
                  ? () =>
                      setPromoDetailsCookie({ ...promoDetails, type: 'image' })
                  : undefined
              }
            >
              {image && <CategoryNavListImage image={image} />}
            </a>
          ) : (
            image && <CategoryNavListImage image={image} />
          )}
        </div>
        <a
          href={`/${locale}${link}`}
          className={styles.categoryNavHeadingLink}
          onClick={
            trackPromos ? () => setPromoDetailsCookie(promoDetails) : undefined
          }
        >
          <Heading
            semanticLevel="h3"
            styleLevel="3"
            className={styles.categoryNavHeading}
          >
            {title}
          </Heading>
        </a>
        <div className={styles.categoryNavContent}>
          <ul className={styles.categoryList}>
            {navLinks?.map(
              ({ category_nav_list_text, category_nav_list_link }) => (
                <li key={`category-nav-link-${category_nav_list_text}`}>
                  <a
                    href={`/${locale}${category_nav_list_link}`}
                    className={styles.categoryNavSubText}
                    onClick={
                      trackPromos
                        ? () =>
                            setPromoDetailsCookie({
                              ...promoDetails,
                              url: category_nav_list_link,
                            })
                        : undefined
                    }
                  >
                    {category_nav_list_text}
                  </a>
                </li>
              )
            )}
          </ul>
          <a
            href={`/${locale}${link}`}
            ref={ref}
            className={styles.categoryNavButton}
            onClick={
              trackPromos
                ? () => setPromoDetailsCookie(promoDetails)
                : undefined
            }
          >
            {text}
            <IconWrapper
              icon="caret-right"
              height={11}
              classNames={styles.categoryNavButtonIcon}
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default CategoryNavigationItem;
